import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Gift, CheckCircle } from 'lucide-react';
import { useSettings } from '@/hooks/useProducts';
import { useAuthStore } from '@/stores/auth-store';
import { formatCurrency } from '@/lib/utils';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';

export default function GiftCards() {
  const { t } = useTranslation('giftCards');
  const { data } = useSettings();
  const { user } = useAuthStore();
  const config = data?.settings?.upsell_config?.gift_cards;

  const denominations = config?.denominations || [10, 25, 50, 100];
  const [selectedAmount, setSelectedAmount] = useState(denominations[1] || 25);
  const [customAmount, setCustomAmount] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [senderName, setSenderName] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState('');

  const amount = customAmount ? Number(customAmount) : selectedAmount;

  async function handlePurchase(e: React.FormEvent) {
    e.preventDefault();
    if (!amount || amount <= 0) return;
    if (!user) { setError(t('errors.signInRequired')); return; }

    setIsSubmitting(true);
    setError('');
    try {
      const res = await api.post<any>('/gift-cards/purchase', {
        amount, recipient_email: recipientEmail, recipient_name: recipientName, sender_name: senderName, message,
      });
      setSuccess(res.code);
    } catch {
      setError(t('errors.purchaseFailed'));
    } finally {
      setIsSubmitting(false);
    }
  }

  if (!config?.enabled) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <Gift className="h-16 w-16 text-ink-muted mx-auto mb-4" aria-hidden="true" />
        <h1 className="text-2xl font-bold text-ink">{t('heading')}</h1>
        <p className="mt-2 text-ink-muted">{t('unavailable')}</p>
      </div>
    );
  }

  if (success) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <CheckCircle className="h-16 w-16 text-sage-500 mx-auto mb-4" aria-hidden="true" />
        <h1 className="text-2xl font-bold text-ink mb-2">{t('purchasedHeading')}</h1>
        <p className="text-ink-soft mb-4">{t('yourCodeIs')}</p>
        <div className="bg-sage-50 border-2 border-sage-600 rounded-xl p-6 inline-block">
          <p className="text-3xl font-bold text-sage-900 font-mono tracking-wider">{success}</p>
          <p className="text-sm text-sage-700 mt-2">
            {t('valueLabel', { amount: formatCurrency(amount) })}
          </p>
        </div>
        {recipientEmail && (
          <p className="mt-4 text-sm text-ink-muted">
            {t('emailSentTo', { email: recipientEmail })}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
      <div className="text-center mb-8">
        <Gift className="h-12 w-12 text-sage-600 mx-auto mb-3" aria-hidden="true" />
        <h1 className="text-3xl font-display font-bold text-ink">{t('heading')}</h1>
        <p className="mt-2 text-ink-soft">
          {data?.settings?.gift_cards_subtitle || t('subtitleDefault')}
        </p>
      </div>

      <form onSubmit={handlePurchase} className="space-y-6">
        <div className="bg-surface rounded-xl border border-line p-6">
          <h2 className="font-semibold text-ink mb-4">{t('form.selectAmount')}</h2>
          <div className="flex flex-wrap gap-3 mb-4">
            {denominations.map((d: number) => (
              <button
                key={d}
                type="button"
                onClick={() => { setSelectedAmount(d); setCustomAmount(''); }}
                className={`px-6 py-3 rounded-lg border-2 font-semibold transition-colors ${
                  selectedAmount === d && !customAmount
                    ? 'border-sage-600 bg-sage-50 text-sage-800'
                    : 'border-line text-ink-soft hover:border-line-strong'
                }`}
              >
                {formatCurrency(d)}
              </button>
            ))}
          </div>
          <Input
            label={t('form.customAmount')}
            type="number"
            value={customAmount}
            onChange={(e) => { setCustomAmount(e.target.value); }}
            placeholder={t('form.customAmountPlaceholder')}
            min="1"
            step="0.01"
          />
        </div>

        <div className="bg-surface rounded-xl border border-line p-6 space-y-4">
          <h2 className="font-semibold text-ink">{t('form.recipientHeading')}</h2>
          <Input
            label={t('form.recipientEmail')}
            type="email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            placeholder={t('form.recipientEmailPlaceholder')}
          />
          <Input
            label={t('form.recipientName')}
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
            placeholder={t('form.recipientNamePlaceholder')}
          />
          <Input
            label={t('form.senderName')}
            value={senderName}
            onChange={(e) => setSenderName(e.target.value)}
            placeholder={t('form.senderNamePlaceholder')}
          />
          <div>
            <label className="block text-sm font-medium text-ink-soft mb-1">
              {t('form.personalMessage')}
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('form.personalMessagePlaceholder')}
              rows={3}
              className="w-full px-3 py-2 border border-line-strong rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sage-500"
            />
          </div>
        </div>

        {error && <p className="text-red-600 text-sm" role="alert">{error}</p>}

        <Button type="submit" size="lg" className="w-full" isLoading={isSubmitting} disabled={!amount || amount <= 0}>
          {t('form.submit', { amount: formatCurrency(amount || 0) })}
        </Button>
      </form>
    </div>
  );
}
